/* ============================================================================
   DVL - unstick the SPO kit drawer after a back-navigation
   ----------------------------------------------------------------------------
   The "Customize your order" drawer belongs to the Shopify Product Options app
   (cdn.shopify.com/extensions/.../spo-product-options-416/spo-presentation.js).
   Two things there combine into a dead end:

     1. open() pushes a history entry, so "product page -> drawer -> /cart"
        leaves TWO entries behind. Back from the cart lands on the drawer entry.
     2. On a successful add the drawer keeps its busy state on purpose — its own
        comment reads "the cart add is navigating away; keep the busy state until
        the page unloads". With the back/forward cache the page is FROZEN, not
        unloaded, so Back restores it exactly as it was left: the body dimmed by
        .spo-overlay--submitting, the submit button disabled reading "Adding...",
        and the Back button disabled too. close() then refuses to run — it bails
        out while _submitting is true — so the backdrop, Escape and the ✕ are all
        dead as well. Nothing in the app listens for `pageshow`, and popstate does
        not fire for a cross-document restore, so nothing ever clears it.

   The shopper's way out is to close the drawer and see the product page, which is
   what a Back press should have given them. We do that here rather than in the
   app: SPOPresentation is on `window`, so clearing its busy flag re-enables the
   controls and lets its own close() run. Every call is guarded — if the app
   renames these, this quietly does nothing and the page is no worse off.

   Remove this once the app clears its own busy state on a bfcache restore.
   ========================================================================== */
(function () {
  'use strict';

  function unstick() {
    var P = window.SPOPresentation;
    if (!P || !P._submitting) return;
    try {
      if (typeof P._setSubmitting === 'function') P._setSubmitting(false);
      else P._submitting = false;
      // Submitting MOVED the add-ons flagged showOnAddonsPage out of the drawer and into the
      // product form, so FormData could collect them (_reunifyForSubmit). Only _relocateFlagged
      // puts them back. Skip it and they stay on the product page — which is the one place a
      // showOnAddonsPage add-on is not supposed to appear. The app's own recovery path
      // (_restoreAfterFailedSubmit) calls it for the same reason; we follow it, then close.
      if (typeof P._relocateFlagged === 'function') P._relocateFlagged();
      if (typeof P.close === 'function') P.close();
    } catch (e) {
      /* the drawer is the app's; if its shape changed, leave the page alone */
    }
    // Belt and braces: if close() left the shell up, drop the classes by hand so
    // the page is at least scrollable and readable again.
    var shell = document.querySelector('.spo-overlay--submitting, .spo-overlay--open');
    if (shell) {
      shell.classList.remove('spo-overlay--submitting', 'spo-overlay--open');
      shell.setAttribute('aria-busy', 'false');
    }
    document.body.classList.remove('spo-overlay-lock');
  }

  // Two ways a shopper arrives at the jammed drawer, and both are covered:
  //
  //   pageshow + persisted — the page came back from the back/forward cache with
  //     the busy state frozen into it. A normal load re-initialises the app and
  //     needs nothing, hence the `persisted` guard.
  //   popstate — the drawer pushed its own history entry, so Back can land on it
  //     without any page load at all. If the add never resolved (a failed or hung
  //     cart request) the busy state is still on, and the app's own popstate
  //     handler calls close(), which bails out on exactly that flag. We clear it
  //     first; this script is loaded before the drawer opens, so our listener runs
  //     before the app's and its close() then succeeds.
  //
  // Only a drawer that is ALREADY stuck is touched — unstick() returns immediately
  // unless _submitting is set — so a genuine in-flight add is left alone.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    unstick();
    // The app restores its own state asynchronously on some themes, so check once
    // more after it has had a tick to run.
    setTimeout(unstick, 250);
  });

  window.addEventListener('popstate', function () {
    unstick();
    setTimeout(unstick, 250);
  });
})();
